import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, CloseIcon, LeftCaretIcon } from '@streamr/tv-ui';
import { executeSoon } from '@streamr/tv-utils';
import cx from 'classnames';
import { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { DestinationsService } from '../../../services/destinations/DestinationsService';
import { PLATFORM_SERVERS, } from '../../../services/destinations/platformServers';
import { Popup, TopRight } from '../../../utils/popups';
import { AbstractPopup, DismissalReason, } from '../AbstractPopup';
import { ScrollablePopupContainer } from '../helpers/ScrollablePopupContainer';
import { PopupCssPreset } from '../Popup.styles';
import { LinkedInIcon, TwitchIcon, XIcon, YouTubeIcon } from './PlatformLogos';
const PLATFORMS = [
    { id: 'youtube', name: 'Youtube', icon: _jsx(YouTubeIcon, {}) },
    { id: 'twitch', name: 'Twitch', icon: _jsx(TwitchIcon, {}) },
    { id: 'x', name: 'X', icon: _jsx(XIcon, {}) },
    { id: 'linkedin', name: 'LinkedIn', icon: _jsx(LinkedInIcon, {}) },
];
function DestinationsPopup({ reject, variant, onBack, ...props }) {
    const destinations = DestinationsService.getOutput().useDestinations();
    const enabled = Object.values(destinations).some((d) => d?.enabled);
    const handleToggle = async () => {
        const next = !enabled;
        const service = DestinationsService.getInstance();
        if (next) {
            for (const platform of PLATFORMS) {
                if (destinations[platform.id]?.config &&
                    !destinations[platform.id]?.enabled) {
                    await service.enableDestination(platform.id);
                }
            }
        }
        else {
            for (const platform of PLATFORMS) {
                if (destinations[platform.id]?.enabled) {
                    await service.disableDestination(platform.id);
                }
            }
        }
    };
    return (_jsx(DestinationsPopupRoot, { reject: reject, translateFn: variant === 'desktop' ? TopRight : undefined, ...props, className: cx(props.className, variant), children: _jsx(ScrollablePopupContainer, { fullscreenOnly: variant === 'mobile', onCloseButtonClick: () => {
                reject(DismissalReason.CloseButton);
            }, children: _jsxs(ContentRoot, { children: [_jsxs(HeaderRow, { children: [_jsx(BackButton, { type: "button", onClick: () => {
                                    reject(DismissalReason.CloseButton);
                                    onBack?.();
                                }, children: _jsx(LeftCaretIcon, {}) }), _jsx(Title, { children: "3rd party destinations" }), _jsx(CloseButton, { type: "button", onClick: () => reject(DismissalReason.CloseButton), children: _jsx(CloseIcon, {}) })] }), _jsxs(SwitchRow, { children: [_jsx(SwitchLabel, { children: "Stream to 3rd party destinations" }), _jsx(ToggleSwitch, { "$active": enabled, type: "button", onClick: handleToggle, children: _jsx(ToggleKnob, { "$active": enabled }) })] }), _jsx(PlatformList, { children: PLATFORMS.map((platform) => (_jsx(PlatformRowItem, { platform: platform, state: destinations[platform.id], streamingEnabled: enabled }, platform.id))) })] }) }) }));
}
function PlatformRowItem({ platform, state, streamingEnabled, }) {
    const [editing, setEditing] = useState(false);
    const [showError, setShowError] = useState(false);
    const isConfigured = !!state?.config;
    const status = state?.status ?? 'disconnected';
    const handleConfigure = () => {
        setEditing(true);
    };
    const handleCancelEdit = () => {
        setEditing(false);
    };
    const handleSaveAndConnect = async (serverUrl, streamKey) => {
        const service = DestinationsService.getInstance();
        service.configureDestination(platform.id, serverUrl, streamKey);
        setEditing(false);
        if (streamingEnabled) {
            await service.enableDestination(platform.id);
        }
    };
    const handleSave = (serverUrl, streamKey) => {
        const service = DestinationsService.getInstance();
        service.configureDestination(platform.id, serverUrl, streamKey);
        setEditing(false);
    };
    const handleEnable = async () => {
        await DestinationsService.getInstance().enableDestination(platform.id);
    };
    const handleDisable = async () => {
        await DestinationsService.getInstance().disableDestination(platform.id);
    };
    const handleRetry = async () => {
        const service = DestinationsService.getInstance();
        await service.disableDestination(platform.id);
        await service.enableDestination(platform.id);
    };
    const handleRemove = async () => {
        setEditing(false);
        setShowError(false);
        await DestinationsService.getInstance().removeDestination(platform.id);
    };
    if (editing) {
        return (_jsxs(PlatformRow, { children: [_jsxs(PlatformInfo, { children: [_jsx(IconContainer, { children: platform.icon }), _jsx(PlatformName, { children: platform.name })] }), _jsx(ConfigForm, { platformId: platform.id, initialServerUrl: state?.config?.serverUrl ?? '', initialStreamKey: state?.config?.streamKey ?? '', isConfigured: isConfigured, onSaveAndConnect: handleSaveAndConnect, onSave: handleSave, onCancel: handleCancelEdit, onRemove: handleRemove })] }));
    }
    if (!isConfigured) {
        return (_jsxs(PlatformRow, { children: [_jsxs(PlatformInfo, { children: [_jsx(IconContainer, { children: platform.icon }), _jsx(PlatformName, { children: platform.name })] }), _jsx(ActionButton, { type: "button", onClick: handleConfigure, children: "Configure" })] }));
    }
    return (_jsxs(PlatformRow, { children: [_jsxs(PlatformInfo, { children: [_jsx(IconContainer, { children: platform.icon }), _jsx(PlatformName, { children: platform.name }), _jsx(StatusBadge, { status: status, onClick: () => {
                            if (status === 'error') {
                                setShowError(!showError);
                            }
                        } })] }), _jsxs(RowActions, { children: [_jsx(GearButton, { type: "button", onClick: handleConfigure, children: _jsx(GearIcon, {}) }), status === 'error' ? (_jsx(ActionButton, { type: "button", onClick: handleRetry, children: "Retry" })) : state?.enabled ? (_jsx(DisableButton, { type: "button", onClick: handleDisable, children: "Disable" })) : (_jsx(EnableButton, { type: "button", onClick: handleEnable, children: "Enable" }))] }), showError && state?.error && (_jsx(ErrorDisplay, { children: state.error }))] }));
}
function StatusBadge({ status, onClick, }) {
    switch (status) {
        case 'publishing':
            return _jsx(OnAirBadge, { children: "ON AIR" });
        case 'connecting':
            return _jsx(ConnectingBadge, { children: "Connecting..." });
        case 'error':
            return (_jsx(ErrorBadge, { type: "button", onClick: onClick, children: "ERROR" }));
        case 'disconnected':
            return _jsx(StatusDot, { "$color": "#666" });
        default:
            return null;
    }
}
function ConfigForm({ platformId, initialServerUrl, initialStreamKey, isConfigured, onSaveAndConnect, onSave, onCancel, onRemove, }) {
    const servers = PLATFORM_SERVERS[platformId];
    const hasServerList = servers.length > 0;
    const isKnownServer = (url) => servers.some((s) => s.url === url);
    const [selectedServer, setSelectedServer] = useState(() => {
        if (!hasServerList) {
            return '';
        }
        if (initialServerUrl && isKnownServer(initialServerUrl)) {
            return initialServerUrl;
        }
        if (initialServerUrl) {
            return '__custom';
        }
        return servers[0].url;
    });
    const [customUrl, setCustomUrl] = useState(() => initialServerUrl && !isKnownServer(initialServerUrl)
        ? initialServerUrl
        : '');
    const [streamKey, setStreamKey] = useState(initialStreamKey);
    const [showKey, setShowKey] = useState(false);
    const effectiveServerUrl = hasServerList && selectedServer === '__custom'
        ? customUrl
        : hasServerList
            ? selectedServer
            : customUrl;
    const canSubmit = effectiveServerUrl.trim() !== '' && streamKey.trim() !== '';
    return (_jsxs(FormContainer, { children: [_jsxs(FormField, { children: [_jsx(FormLabel, { children: "Server" }), hasServerList ? (_jsxs(FormSelect, { value: selectedServer, onChange: (e) => setSelectedServer(e.target.value), children: [servers.map((s) => (_jsx("option", { value: s.url, children: s.label }, s.url))), _jsx("option", { value: "__custom", children: "Custom" })] })) : (_jsx(FormInput, { type: "text", placeholder: "rtmp://...", value: customUrl, onChange: (e) => setCustomUrl(e.target.value) })), hasServerList && selectedServer === '__custom' && (_jsx(FormInput, { type: "text", placeholder: "rtmp://...", value: customUrl, onChange: (e) => setCustomUrl(e.target.value) }))] }), _jsxs(FormField, { children: [_jsx(FormLabel, { children: "Stream Key" }), _jsxs(StreamKeyRow, { children: [_jsx(FormInput, { type: showKey ? 'text' : 'password', placeholder: "Enter stream key", value: streamKey, onChange: (e) => setStreamKey(e.target.value) }), _jsx(ShowKeyButton, { type: "button", onClick: () => setShowKey(!showKey), children: showKey ? 'Hide' : 'Show' })] })] }), _jsxs(FormActions, { children: [_jsx(ActionButton, { type: "button", onClick: () => onSaveAndConnect(effectiveServerUrl, streamKey), disabled: !canSubmit, children: "Connect" }), _jsx(ActionButton, { type: "button", onClick: () => onSave(effectiveServerUrl, streamKey), disabled: !canSubmit, children: "Save" }), _jsx(CancelButton, { type: "button", onClick: onCancel, children: "Cancel" }), isConfigured && (_jsx(RemoveButton, { type: "button", onClick: onRemove, children: "Remove" }))] })] }));
}
function GearIcon() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "currentColor", children: [_jsx("title", { children: "Settings" }), _jsx("path", { d: "M6.837 1.2a.75.75 0 0 1 .738-.6h.85a.75.75 0 0 1 .738.6l.206 1.14a5.25 5.25 0 0 1 1.283.74l1.095-.38a.75.75 0 0 1 .878.338l.425.736a.75.75 0 0 1-.14.938l-.89.76a5.3 5.3 0 0 1 0 1.476l.89.76a.75.75 0 0 1 .14.938l-.425.736a.75.75 0 0 1-.878.338l-1.095-.38a5.25 5.25 0 0 1-1.283.74l-.206 1.14a.75.75 0 0 1-.738.6h-.85a.75.75 0 0 1-.738-.6l-.206-1.14a5.25 5.25 0 0 1-1.283-.74l-1.095.38a.75.75 0 0 1-.878-.338l-.425-.736a.75.75 0 0 1 .14-.938l.89-.76a5.3 5.3 0 0 1 0-1.476l-.89-.76a.75.75 0 0 1-.14-.938l.425-.736a.75.75 0 0 1 .878-.338l1.095.38a5.25 5.25 0 0 1 1.283-.74l.206-1.14ZM8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" })] }));
}
// #region Styled Components
const DestinationsPopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    &.desktop {
        @media (min-width: 30rem) {
            ${PopupCssPreset.AnchoredTopRight};
            --Popup_Width: 25rem;
            --Popup_Height: calc(var(--vh, 1vh) * 100 - 8rem);
            --Popup_Padding: 1rem 0;
        }
    }

    &.mobile {
        --SettingsContainer_InnerMaxWidth: 25rem;
    }
`;
const ContentRoot = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;
const HeaderRow = styled.div `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
`;
const BackButton = styled(Button) `
    --Button_Padding: 0.5rem;
    --Button_HoverTransform: none;
    --Button_ActiveTransform: none;
    --Button_Background: rgba(231, 231, 231, 0.2);
    --Button_BorderRadius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
`;
const Title = styled.span `
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.5rem;
    flex: 1;
`;
const CloseButton = styled(Button) `
    --Button_Padding: 0.5rem;
    --Button_HoverTransform: none;
    --Button_ActiveTransform: none;
    --Button_Background: rgba(231, 231, 231, 0.2);
    --Button_BorderRadius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
`;
const SwitchRow = styled.div `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
`;
const SwitchLabel = styled.span `
    font-size: 0.9375rem;
    font-weight: 400;
    line-height: 1.375rem;
    color: white;
`;
const ToggleSwitch = styled.button `
    position: relative;
    width: 2.75rem;
    height: 1.5rem;
    border-radius: 0.75rem;
    border: none;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s;
    background: ${(p) => p.$active ? 'rgba(34, 197, 94, 0.6)' : 'rgba(255, 255, 255, 0.2)'};
`;
const ToggleKnob = styled.div `
    position: absolute;
    top: 0.1875rem;
    left: ${(p) => (p.$active ? 'calc(100% - 1.3125rem)' : '0.1875rem')};
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
`;
const PlatformList = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;
const PlatformRow = styled.div `
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: 1rem;
    border-radius: 0.75rem;
    border: 0.5px solid rgba(255, 255, 255, 0.4);
    position: relative;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 0.75rem;
        background: rgba(255, 255, 255, 0.06);
        mix-blend-mode: lighten;
        pointer-events: none;
    }

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 0.75rem;
        background: rgba(94, 94, 94, 0.18);
        mix-blend-mode: color-dodge;
        pointer-events: none;
    }
`;
const PlatformInfo = styled.div `
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;
const IconContainer = styled.div `
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;
const PlatformName = styled.span `
    font-size: 0.9375rem;
    font-weight: 500;
    line-height: 1.375rem;
    color: white;
`;
const RowActions = styled.div `
    display: flex;
    align-items: center;
    gap: 0.5rem;
    position: relative;
    z-index: 1;
`;
const buttonBase = css `
    --Button_Padding: 0.375rem 0.5rem;
    --Button_HoverTransform: none;
    --Button_ActiveTransform: none;
    --Button_BorderRadius: 0.5rem;
    --Button_TextSize: 0.8125rem;
    font-weight: 500;
    position: relative;
    z-index: 1;
`;
const ActionButton = styled(Button) `
    ${buttonBase}
    --Button_Background: #2f2f2f;
    --Button_HoverBackground: #3a3a3a;
    border: 1px solid rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(50px);

    &:disabled {
        opacity: 0.4;
        pointer-events: none;
    }
`;
const EnableButton = styled(Button) `
    ${buttonBase}
    --Button_Background: rgba(34, 197, 94, 0.25);
    --Button_HoverBackground: rgba(34, 197, 94, 0.4);
    border: 1px solid rgba(34, 197, 94, 0.6);
    color: #22c55e;
`;
const DisableButton = styled(Button) `
    ${buttonBase}
    --Button_Background: rgba(239, 68, 68, 0.2);
    --Button_HoverBackground: rgba(239, 68, 68, 0.35);
    border: 1px solid rgba(239, 68, 68, 0.5);
    color: #ef4444;
`;
const CancelButton = styled(Button) `
    ${buttonBase}
    --Button_Background: transparent;
    --Button_HoverBackground: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
`;
const RemoveButton = styled(Button) `
    ${buttonBase}
    --Button_Background: transparent;
    --Button_HoverBackground: rgba(239, 68, 68, 0.2);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #ef4444;
`;
const GearButton = styled(Button) `
    --Button_Padding: 0.375rem;
    --Button_HoverTransform: none;
    --Button_ActiveTransform: none;
    --Button_Background: transparent;
    --Button_HoverBackground: rgba(255, 255, 255, 0.1);
    --Button_BorderRadius: 0.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.7);
    position: relative;
    z-index: 1;
`;
const OnAirBadge = styled.span `
    font-size: 0.6875rem;
    font-weight: 700;
    color: #22c55e;
    background: rgba(34, 197, 94, 0.2);
    border: 1px solid rgba(34, 197, 94, 0.5);
    border-radius: 0.25rem;
    padding: 0.125rem 0.375rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;
const pulse = keyframes `
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
`;
const ConnectingBadge = styled.span `
    font-size: 0.6875rem;
    font-weight: 500;
    color: #eab308;
    animation: ${pulse} 1.5s ease-in-out infinite;
`;
const ErrorBadge = styled.button `
    font-size: 0.6875rem;
    font-weight: 700;
    color: #ef4444;
    background: rgba(239, 68, 68, 0.2);
    border: 1px solid rgba(239, 68, 68, 0.5);
    border-radius: 0.25rem;
    padding: 0.125rem 0.375rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    cursor: pointer;
`;
const StatusDot = styled.div `
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: ${(p) => p.$color};
`;
const ErrorDisplay = styled.div `
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 0.5rem;
    color: #fca5a5;
    font-size: 0.8125rem;
    line-height: 1.25rem;
    word-break: break-word;
    position: relative;
    z-index: 1;
`;
const FormContainer = styled.div `
    width: 100%;
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    position: relative;
    z-index: 1;
`;
const FormField = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;
const FormLabel = styled.label `
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;
const inputStyles = css `
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.375rem;
    padding: 0.5rem 0.625rem;
    color: white;
    font-size: 0.8125rem;
    outline: none;
    width: 100%;
    box-sizing: border-box;

    &:focus {
        border-color: rgba(255, 255, 255, 0.5);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.3);
    }
`;
const FormInput = styled.input `
    ${inputStyles}
`;
const FormSelect = styled.select `
    ${inputStyles}
    cursor: pointer;

    option {
        background: #1a1a1a;
        color: white;
    }
`;
const StreamKeyRow = styled.div `
    display: flex;
    gap: 0.375rem;

    ${FormInput} {
        flex: 1;
    }
`;
const ShowKeyButton = styled(Button) `
    ${buttonBase}
    --Button_Background: rgba(255, 255, 255, 0.1);
    --Button_HoverBackground: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.2);
    flex-shrink: 0;
    font-size: 0.75rem;
`;
const FormActions = styled.div `
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;
// #endregion
const destinationsPopup = Popup.create(DestinationsPopup);
export function showDestinationsPopup({ anchorRef, variant, onBack, }) {
    executeSoon(async () => {
        try {
            await destinationsPopup.pop({ anchorRef, variant, onBack });
        }
        catch (_) { }
    });
}
